import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { isResponseContainFlightsBody } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import { isRadarFlightsResponseError } from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/radarFlightsResponse';

import type { RadarItinerary } from '@web-platform/shared-universal-types/src/network/endpoints/radar/radarItineraryTypes';
import type { Action } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type { CurrentFlightItineraryIdsState } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entityCollections/currentFlightItineraryIds';

const INITIAL_STATE: CurrentFlightItineraryIdsState = [];

// Tracks (Radar sessionId, itineraryId) pairs we've already warned about, so the
// USE_ITINERARY_TOKEN observability log fires at most once per missing-token
// itinerary per Radar session — independent of how many times we poll that session.
const warnedRadarSessionItineraryPairs = new Set<string>();

export default function currentFlightItineraryIdsReducer(
  state: CurrentFlightItineraryIdsState = INITIAL_STATE,
  action: Action,
): CurrentFlightItineraryIdsState {
  switch (action.type) {
    case MUTATION_RESPONSE_RECEIVED:
    case QUERY_RESPONSE_RECEIVED: {
      if (action.payload.type === RADAR_CREATE_SESSIONS || action.payload.type === RADAR_POLL) {
        if (
          isRadarFlightsResponseError(action.payload.response) ||
          !isResponseContainFlightsBody(action.payload.response)
        ) {
          return state;
        }

        const itineraries = action.payload.response.body.itineraries.results || [];
        const useToken = getFeatureDecision(FEATURE_DECISION_KEYS.USE_ITINERARY_TOKEN);

        if (useToken) {
          const { sessionId: radarSessionId } = action.payload.response.body.itineraries.context;
          if (radarSessionId) {
            itineraries.forEach((itinerary) => {
              if (itinerary.token) return;
              const key = `${radarSessionId}|${itinerary.id}`;
              if (warnedRadarSessionItineraryPairs.has(key)) return;
              warnedRadarSessionItineraryPairs.add(key);
              logWarn({
                component: 'currentFlightItineraryIds',
                message: 'Itinerary token is missing when USE_ITINERARY_TOKEN is enabled',
                itineraryId: itinerary.id,
              });
            });
          }
        }

        return itineraries.map((itinerary: RadarItinerary) => {
          if (useToken && itinerary.token) {
            return itinerary.token;
          }
          return itinerary.id;
        });
      }

      return state;
    }

    case SEARCH_PARAMS_CHANGED:
      return [];

    default:
      return state;
  }
}
export const getAll = (state: CurrentFlightItineraryIdsState): CurrentFlightItineraryIdsState =>
  state;
